import {
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  linkWithCredential,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  type User,
  type UserCredential,
} from "firebase/auth";
import { getFirebaseAuth } from "../lib/firebase";
import { PAYMENTS_ENABLED } from "../config/payments";
import { checkoutInFlight, claimGuestIdentity, guestRotationSettled, identityCreatedAt, isRetirementPending } from "../lib/guestSession";

/*
 * BOOK'S EMAIL-AND-PASSWORD FIREBASE CALLS, out of the views (c4: moved
 * unchanged from BookSignIn, BookRecovery and AccountCreate, whose screens
 * keep their own state, copy and error mapping). Views reach the Firebase SDK
 * through auth/ and lib/ modules only (`firebase-in-views`,
 * scripts/lib/one-way-contracts.js). Provider sign-in is auth/providerSignIn.ts.
 */

/** Email sign-in. The caller releases a guest's hold first: the UID is about to change. */
export function signInWithEmail(email: string, password: string): Promise<UserCredential> {
  return signInWithEmailAndPassword(getFirebaseAuth(), email, password);
}

/**
 * Ask Firebase to email a password-reset link, in `languageCode`.
 * `continueUrl` is the canonical environment-correct sign-in URL, never one
 * carrying an email, token or booking data. Firebase's app.* action handler
 * owns the one-use link; this never signs anyone in or out, creates an
 * account, or replaces Book's anonymous UID.
 *
 * Not async on purpose: a synchronous failure reaches the caller's `try`
 * exactly where it did before the move.
 */
export function sendPasswordReset(email: string, languageCode: string, continueUrl: string): Promise<void> {
  const auth = getFirebaseAuth();
  auth.languageCode = languageCode;
  return sendPasswordResetEmail(auth, email, { url: continueUrl, handleCodeInApp: false });
}

/**
 * Firebase's configured action handler verifies the email on app.*. It does
 * not transfer this Book session or follow continueUrl: the client returns to
 * the still-open Book tab, which checks the refreshed token there.
 */
export function sendVerificationEmail(user: User): Promise<void> {
  return sendEmailVerification(user);
}

/** The deliberate sign-out ("Use another account"). */
export function signOutClient(): Promise<void> {
  return signOut(getFirebaseAuth());
}

/**
 * THE EMAIL ACCOUNT'S FIREBASE IDENTITY: link the anonymous booking identity,
 * or create a new one. `email` is passed already trimmed.
 *
 * A rotation under way decides which identity is current, so it is read
 * after it settles. An anonymous guest is LINKED rather than replaced: its
 * UID survives, and its existing appointments and management ownership stay
 * theirs. Claimed while the link is on the wire: an idle rotation landing in
 * between would link this account to a UID that is no longer current.
 *
 * NEVER an identity whose retirement is pending (security audit F1): it made
 * a booking on this device, possibly someone else's, and linking would hand
 * that booking to this account. A fresh account replaces it, and that
 * guest's hold is released under its own token first (as the provider path
 * does), or the slot stays blocked until it expires. Nothing to release when
 * there is no guest.
 */
export async function createOrLinkEmailAccount(
  email: string,
  password: string,
  releaseHold?: () => Promise<void>
): Promise<User> {
  const auth = getFirebaseAuth();
  await guestRotationSettled();
  const unclaim = claimGuestIdentity();
  try {
    const guest = auth.currentUser;
    // Never linked while a hosted checkout is in flight for it either (H1).
    if (guest?.isAnonymous && !isRetirementPending() && !(PAYMENTS_ENABLED && checkoutInFlight(undefined, Date.now(), identityCreatedAt(guest)))) {
      return (await linkWithCredential(guest, EmailAuthProvider.credential(email, password))).user;
    }
    if (guest?.isAnonymous && releaseHold) await releaseHold().catch(() => undefined);
    return (await createUserWithEmailAndPassword(auth, email, password)).user;
  } finally {
    unclaim();
  }
}
